/* mofumiya herd builder.
   Three steps in a native <dialog>: pick cows, add extras, review.
   Like the best converting builders: a free-shipping progress bar under every step, the plushies go into the real cart
   as soon as the customer moves past step 1 (so a closed builder still leaves a cart), extras are added at the end,
   and the last button goes straight to checkout.
   The herd price and the shipping rates are real Shopify settings; this file only previews them with the same numbers. */

(() => {
  if (customElements.get('mofu-builder')) return;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const formatMoney = (cents, format) => {
    const value = Math.round(cents);
    const fmt = format || '${{amount}}';
    const withDelims = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intPart = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? intPart + decimal + dec : intPart;
    };
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    let out;
    switch (key) {
      case 'amount_no_decimals': out = withDelims(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelims(value, 0, '.', ','); break;
      default: out = withDelims(value, 2, ',', '.');
    }
    // Drop ".00" so prices read like "$59", not "$59.00"
    out = out.replace(/[.,]00$/, '');
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  };

  // The store format is in dollars; a sample price printed by Liquid gives the visitor's own currency format
  const formatFromProbe = (probe) => {
    if (!probe) return '';
    const text = new DOMParser().parseFromString(String(probe), 'text/html').documentElement.textContent;
    if (text.includes('1,00')) return text.replace('1,00', '{{amount_with_comma_separator}}');
    if (text.includes('1.00')) return text.replace('1.00', '{{amount}}');
    if (/(^|\D)1(\D|$)/.test(text)) return text.replace(/(^|\D)1(\D|$)/, '$1{{amount_no_decimals}}$2');
    return '';
  };
  const dateLocale = (lang) => ({ en: 'en-US', de: 'de-DE' })[lang] || lang || 'en-US';
  const fmtDate = (d, lang) => d.toLocaleDateString(dateLocale(lang), { month: 'short', day: 'numeric' });
  // English fallback, used only if the section did not send its translated strings
  const EN = {
    in_a_herd: "in a herd",
    adding_list: "Adding: {list}",
    hint_empty: "Every {unit} is {price}. From {qty} {units}, each one is {per}.",
    hint_more: "Add {n} more and every {unit} drops to {per}.",
    hint_on: "Herd price on: every {unit} is {per}. You save {saving}.",
    ship_from_amount: "Free shipping from {amount}",
    ship_add_amount: "Add {amount} more for free shipping",
    ship_from_qty: "Free shipping from {qty} {units}",
    ship_add_qty: "Add {n} more {unit} for free shipping",
    ship_unlocked: "You’ve unlocked free shipping ♡",
    deal_empty: "{qty}+ {units}: {per} each ♡",
    deal_more: "Add a second {unit} and both drop to {per} ♡",
    deal_on: "Herd price on: you’re saving {saving} ♡",
    ship_cost: "Shipping {amount}",
    empty: "Your herd is empty",
    in_cart_suffix: " in your cart",
    next_continue: "Continue",
    next_review: "Review my herd",
    next_checkout: "Checkout",
    each: "each",
    remove_one: "Remove one {name}",
    add_one_more: "Add one more {name}",
    label_in_cart: "In your cart",
    pick_first: "Pick at least one first.",
    label_adding: "Adding now",
    sum_herd_price: "Herd price",
    sum_extras: "Extras",
    sum_shipping: "Shipping",
    sum_free: "Free",
    sum_total: "Total",
    sum_add_amount: "Add {amount} more and shipping is free.",
    sum_add_qty: "Add {n} more {unit} and shipping is free.",
    arrives: "Arrives {from} – {to}",
    to_checkout: "Taking you to checkout…",
    to_cart: "Adding your herd to the cart…",
    cart_error: "Couldn't update the cart: {error}. Please try again.",
    ship_free: "Free US shipping",
  };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  class MofuBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.data = data;
      this.money = formatFromProbe(data.moneyProbe) || data.moneyFormat;
      this.i18n = data.i18n || {};
      this.herd = new Map(); // cow variant id -> qty
      this.extras = new Map(); // extra variant id -> qty
      this.inCart = new Map(); // what the cart already holds, for our variants
      this.picked = new Map(); // extra key -> variant id chosen with the color chips
      (data.extras || []).forEach((x) => {
        const first = x.variants.find((v) => v.available) || x.variants[0];
        if (first) this.picked.set(x.key, first.id);
      });
      this.cartLoaded = false;
      this.dealShown = false;
      this.step = 1;
      this.dialog = this.querySelector('dialog');
      this.live = this.querySelector('[data-live]');
      this.opener = null;

      this.addEventListener('click', (e) => this.onClick(e));
      this.dialog.addEventListener('close', () => this.onClose());
      this.dialog.addEventListener('cancel', () => this.onClose());
      // Close on backdrop click
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.close();
      });

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-mofu-open-builder]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger.getAttribute('data-cow') || '', trigger);
      });

      this.renderDates();
      this.render();

      // Deep links: ?cow=Kinako or #build opens the builder straight away
      const params = new URLSearchParams(location.search);
      const cow = params.get('cow');
      if (cow || location.hash === '#build') {
        // setTimeout, not requestAnimationFrame: rAF never fires in a tab opened in the background
        setTimeout(() => this.open(cow || '', null), 0);
      }
    }

    // Strings come translated from the section (locales/*.json, "mofu.js"); {name} placeholders are filled here
    tr(key, vars = {}) {
      const text = this.i18n[key] || EN[key] || key;
      return text.replace(/\{(\w+)\}/g, (all, k) => (k in vars ? vars[k] : all));
    }

    /* ---------- lookups ---------- */

    cowById(id) { return this.data.cows.find((c) => c.id === id); }
    variantByName(name) {
      const n = String(name).trim().toLowerCase();
      return this.data.cows.find((c) => c.name.toLowerCase() === n);
    }
    extraOf(key) { return (this.data.extras || []).find((x) => x.key === key); }
    extraVariant(id) {
      for (const x of this.data.extras || []) {
        const v = x.variants.find((xv) => xv.id === id);
        if (v) return { x, v };
      }
      return null;
    }
    extraQty(x) { return x.variants.reduce((n, v) => n + (this.extras.get(v.id) || 0), 0); }

    /* ---------- open / close ---------- */

    async open(cowName, opener) {
      this.opener = opener || document.activeElement;
      this.goTo(1);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('mofu-builder-open');
      await this.loadCart();
      if (cowName) {
        const v = this.variantByName(cowName);
        if (v && v.available && !this.herd.get(v.id)) this.herd.set(v.id, 1);
      }
      this.render();
      const first = this.dialog.querySelector('[data-step="1"] .mofu-cow-card button');
      first?.focus({ preventScroll: true });
    }

    // Every close path calls this directly: the dialog 'close' event is not guaranteed to fire promptly
    close() {
      if (this.dialog.open) this.dialog.close();
      this.onClose();
    }

    onClose() {
      if (!document.documentElement.classList.contains('mofu-builder-open')) return;
      document.documentElement.classList.remove('mofu-builder-open');
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
    }

    // Start from what is already in the cart, so a returning customer sees their herd
    async loadCart() {
      if (this.cartLoaded) return;
      this.cartLoaded = true;
      try {
        const res = await Promise.race([
          fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }),
          new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 2500)),
        ]);
        const cart = await res.json();
        const fresh = this.herd.size === 0 && this.extras.size === 0;
        (cart.items || []).forEach((item) => {
          const id = item.variant_id;
          if (this.cowById(id)) {
            this.inCart.set(id, (this.inCart.get(id) || 0) + item.quantity);
            // A cow that sold out stays out of the herd, so the next sync takes it out of the cart
            if (fresh && this.cowById(id).available) this.herd.set(id, (this.herd.get(id) || 0) + item.quantity);
          } else if (this.extraVariant(id)) {
            this.inCart.set(id, (this.inCart.get(id) || 0) + item.quantity);
            if (fresh) this.extras.set(id, (this.extras.get(id) || 0) + item.quantity);
          }
        });
        if (this.herd.size) this.dealShown = true;
      } catch (e) { /* an empty start is fine */ }
    }

    goTo(step) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach((el) => {
        el.hidden = Number(el.dataset.step) !== step;
      });
      this.querySelectorAll('[data-progress]').forEach((el) => {
        const n = Number(el.dataset.progress);
        el.classList.toggle('is-current', n === step);
        el.classList.toggle('is-done', n < step);
        if (n === step) el.setAttribute('aria-current', 'step');
        else el.removeAttribute('aria-current');
      });
      if (step !== 1) this.hideDeal();
      const body = this.querySelector('.mofu-builder__body');
      if (body) body.scrollTop = 0;
      this.render();
    }

    /* ---------- clicks ---------- */

    onClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn || !this.contains(btn)) return;
      const action = btn.dataset.action;
      const id = Number(btn.dataset.id);
      const key = btn.dataset.extra;
      switch (action) {
        case 'close': this.close(); return;
        case 'deal-close': this.hideDeal(); return;
        case 'add': {
          const before = this.boxCount();
          this.herd.set(id, (this.herd.get(id) || 0) + 1);
          this.bump(btn);
          if (before === 0 && this.step === 1) this.showDeal();
          if (this.boxCount() >= this.data.minQty) this.hideDeal();
          break;
        }
        case 'remove': {
          const q = (this.herd.get(id) || 0) - 1;
          if (q > 0) this.herd.set(id, q); else this.herd.delete(id);
          break;
        }
        case 'extra-pick': this.picked.set(key, id); break;
        case 'extra-add': {
          const x = this.extraOf(key);
          const vid = id || this.picked.get(key);
          if (!x || !vid) return;
          if (x.max && this.extraQty(x) >= x.max) return;
          this.extras.set(vid, (this.extras.get(vid) || 0) + 1);
          break;
        }
        case 'extra-remove': {
          const vid = id || this.picked.get(key);
          const q = (this.extras.get(vid) || 0) - 1;
          if (q > 0) this.extras.set(vid, q); else this.extras.delete(vid);
          break;
        }
        case 'favorites': {
          (this.data.favorites || []).forEach((name) => {
            const v = this.variantByName(name);
            if (v && v.available && !this.herd.get(v.id)) this.herd.set(v.id, 1);
          });
          break;
        }
        case 'next': this.next(); return;
        case 'back': this.goTo(Math.max(1, this.step - 1)); return;
        case 'step': this.goTo(Number(btn.dataset.target)); return;
        case 'cart': this.submit(false); return;
        default: return;
      }
      // Past step 1 the boxes are in the cart, so box changes go to the cart too ("In your cart" stays true)
      if (this.step > 1 && (action === 'add' || action === 'remove')) this.scheduleSync();
      this.render();
    }

    scheduleSync() {
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => {
        this.syncCart(false).catch(() => { /* checkout sends the full herd again */ });
      }, 450);
    }

    next() {
      if (this.boxCount() === 0) return;
      if (this.step === 1) {
        // Move on at once. The boxes go into the real cart in the background, so a customer who leaves
        // finds the herd waiting; a failed sync never blocks the steps, checkout sends everything again.
        this.goTo(2);
        this.syncCart(false).catch((err) => console.warn('mofu builder: cart sync', err));
      } else if (this.step === 2) {
        this.goTo(3);
      } else {
        this.submit(true);
      }
    }

    showDeal() {
      if (this.dealShown) return;
      this.dealShown = true;
      const deal = this.querySelector('[data-deal]');
      if (deal) deal.hidden = false;
    }

    hideDeal() {
      const deal = this.querySelector('[data-deal]');
      if (deal) deal.hidden = true;
    }

    bump(btn) {
      const card = btn.closest('.mofu-cow-card');
      if (!card || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      card.classList.remove('is-bump');
      void card.offsetWidth;
      card.classList.add('is-bump');
    }

    // "1 mofu", "2 mofus": the unit names come from the section settings
    unit(n) { return n === 1 ? this.data.unitOne : this.data.unitMany; }

    boxCount() {
      let n = 0;
      this.herd.forEach((q) => { n += q; });
      return n;
    }

    /* ---------- numbers ---------- */

    totals() {
      const d = this.data;
      const n = this.boxCount();
      const tierOn = n >= d.minQty;
      let boxes = 0;
      let compare = 0;
      this.herd.forEach((q, id) => {
        const v = this.cowById(id);
        if (!v) return;
        boxes += v.price * q;
        compare += (v.compare || v.price) * q;
      });
      const herdSaving = tierOn ? n * d.discount : 0;
      let extras = 0;
      this.extras.forEach((q, id) => {
        const hit = this.extraVariant(id);
        if (hit) extras += hit.v.price * q;
      });
      const items = boxes - herdSaving + extras;
      const ship = d.shipping || { rate: 0, freeFrom: 0, freeQty: 0 };
      // A Mystery Mofu is a mofu too when shipping is free from a number of them
      const mystery = this.extraOf('mystery');
      const plush = n + (mystery ? this.extraQty(mystery) : 0);
      let shipping;
      let toFree = 0;
      let toFreeQty = 0;
      if (ship.freeQty) {
        shipping = items > 0 && plush < ship.freeQty ? ship.rate : 0;
        toFreeQty = Math.max(0, ship.freeQty - plush);
      } else {
        shipping = items > 0 && items < ship.freeFrom ? ship.rate : 0;
        toFree = Math.max(0, ship.freeFrom - items);
      }
      return { n, plush, boxes, herdSaving, extras, items, shipping, total: items + shipping, toFree, toFreeQty, tierOn, compare: compare + extras };
    }

    say(msg) {
      if (this.live) this.live.textContent = msg;
    }

    /* ---------- drawing ---------- */

    render() {
      const d = this.data;
      const t = this.totals();
      const m = (c) => formatMoney(c, this.money);
      const per = d.basePrice - d.discount;

      // Cow cards: counts, selected state, price as a box or in a herd
      this.querySelectorAll('.mofu-cow-card').forEach((card) => {
        const id = Number(card.dataset.id);
        const q = this.herd.get(id) || 0;
        card.classList.toggle('is-picked', q > 0);
        const count = card.querySelector('[data-count]');
        if (count) count.textContent = q;
        const stepper = card.querySelector('.mofu-stepper');
        const addBtn = card.querySelector('[data-role="add-first"]');
        if (stepper) stepper.hidden = q === 0;
        if (addBtn) addBtn.hidden = q > 0;
        const price = card.querySelector('[data-price]');
        if (price) {
          if (t.tierOn) price.innerHTML = `<b>${m(per)}</b> <s>${m(d.basePrice)}</s>`;
          else if (t.n > 0 && q === 0) price.innerHTML = `<s>${m(d.basePrice)}</s> <b>${m(per)}</b> ${esc(this.tr('in_a_herd'))}`;
          else price.innerHTML = `<b>${m(d.basePrice)}</b>`;
        }
      });

      // Extras: chips, counts and the add button
      this.querySelectorAll('.mofu-extra').forEach((row) => {
        const x = this.extraOf(row.dataset.extra);
        if (!x) return;
        const vid = this.picked.get(x.key);
        const qSel = this.extras.get(vid) || 0;
        const qAll = this.extraQty(x);
        row.classList.toggle('is-picked', qAll > 0);
        row.querySelectorAll('[data-action="extra-pick"]').forEach((chip) => {
          const cid = Number(chip.dataset.id);
          chip.setAttribute('aria-pressed', String(cid === vid));
          const n = chip.querySelector('[data-chip-count]');
          const cq = this.extras.get(cid) || 0;
          if (n) n.textContent = cq ? `×${cq}` : '';
        });
        const v = x.variants.find((xv) => xv.id === vid);
        const img = row.querySelector('.mofu-extra__img img');
        if (img && v && v.thumb && img.dataset.vid !== String(vid)) {
          img.dataset.vid = String(vid);
          img.removeAttribute('srcset');
          img.src = v.thumb.replace(/width=\d+/, 'width=480');
        }
        const count = row.querySelector('[data-count]');
        if (count) count.textContent = qSel;
        const stepper = row.querySelector('.mofu-stepper');
        const addBtn = row.querySelector('[data-role="add-first"]');
        if (stepper) stepper.hidden = qSel === 0;
        if (addBtn) addBtn.hidden = qSel > 0;
        const more = row.querySelector('[data-role="more"]');
        if (more) more.disabled = !!x.max && qAll >= x.max;
        const sum = row.querySelector('[data-extra-summary]');
        if (sum) {
          const parts = x.variants.filter((xv) => this.extras.get(xv.id)).map((xv) => `${this.extras.get(xv.id)} ${xv.title}`);
          sum.hidden = x.variants.length < 2 || parts.length === 0;
          sum.textContent = parts.length ? this.tr('adding_list', { list: parts.join(', ') }) : '';
        }
      });

      // Tier hint under the grid
      const hint = this.querySelector('[data-tier-hint]');
      if (hint) {
        if (t.n === 0) hint.textContent = this.tr('hint_empty', { unit: d.unitOne, price: m(d.basePrice), qty: d.minQty, units: d.unitMany, per: m(per) });
        else if (!t.tierOn) hint.textContent = this.tr('hint_more', { n: d.minQty - t.n, unit: d.unitOne, per: m(per) });
        else hint.textContent = this.tr('hint_on', { unit: d.unitOne, per: m(per), saving: m(t.herdSaving) });
        hint.classList.toggle('is-on', t.tierOn);
      }

      // "Not sure? Start with our favorites" only helps before the first pick
      const help = this.querySelector('[data-action="favorites"]');
      if (help) help.hidden = t.n > 0;

      // Free shipping progress, in the bar and inline in step 2
      const ship = d.shipping || { freeFrom: 0, freeQty: 0 };
      let shipText;
      let shipFill = 100;
      if (ship.freeQty) {
        if (t.items === 0) shipText = this.tr('ship_from_qty', { qty: ship.freeQty, units: d.unitMany });
        else if (t.toFreeQty > 0) shipText = this.tr('ship_add_qty', { n: t.toFreeQty, unit: this.unit(t.toFreeQty) });
        else shipText = this.tr('ship_unlocked');
        shipFill = Math.min(100, (t.plush / ship.freeQty) * 100);
      } else if (ship.freeFrom) {
        if (t.items === 0) shipText = this.tr('ship_from_amount', { amount: m(ship.freeFrom) });
        else if (t.toFree > 0) shipText = this.tr('ship_add_amount', { amount: m(t.toFree) });
        else shipText = this.tr('ship_unlocked');
        shipFill = Math.min(100, (t.items / ship.freeFrom) * 100);
      } else {
        shipText = '';
      }
      this.querySelectorAll('[data-ship-text]').forEach((el) => { el.textContent = shipText; });
      const fill = this.querySelector('[data-ship-fill]');
      if (fill) fill.style.width = `${shipFill}%`;
      const shipBox = this.querySelector('[data-ship]');
      if (shipBox) shipBox.classList.toggle('is-free', t.items > 0 && t.shipping === 0);
      const deal = this.querySelector('[data-deal-line]');
      if (deal) {
        if (t.n === 0) deal.textContent = this.tr('deal_empty', { qty: d.minQty, units: d.unitMany, per: m(per) });
        else if (!t.tierOn) deal.textContent = this.tr('deal_more', { unit: d.unitOne, per: m(per) });
        else deal.textContent = this.tr('deal_on', { saving: m(t.herdSaving) });
      }
      this.querySelectorAll('[data-ship-short]').forEach((el) => {
        el.textContent = t.shipping ? this.tr('ship_cost', { amount: m(t.shipping) }) : this.tr('ship_free');
      });

      // Bar
      const bar = this.querySelector('.mofu-builder__bar');
      if (bar) {
        const synced = this.step > 1 && t.n > 0;
        bar.querySelector('[data-bar-count]').innerHTML =
          t.n === 0 ? esc(this.tr('empty')) : `${t.n} ${esc(this.unit(t.n))}${synced ? `<span data-in-cart>${esc(this.tr('in_cart_suffix'))}</span>` : ''}`;
        const next = bar.querySelector('[data-action="next"]');
        if (next) {
          next.disabled = t.n === 0;
          const label = this.tr(this.step === 1 ? 'next_continue' : this.step === 2 ? 'next_review' : 'next_checkout');
          next.querySelector('[data-next-label]').textContent = t.n ? `${label} · ${m(t.total)}` : label;
        }
        const alt = bar.querySelector('[data-action="cart"]');
        if (alt) alt.hidden = this.step !== 3 || t.n === 0;
      }
      const back = this.querySelector('[data-action="back"]');
      if (back) back.hidden = this.step === 1;

      if (this.step === 3) this.renderReview(t, m);
    }

    renderReview(t, m) {
      const d = this.data;
      const per = t.tierOn ? d.basePrice - d.discount : d.basePrice;
      const wrap = this.querySelector('[data-review]');
      if (wrap) {
        const boxes = [];
        this.herd.forEach((q, id) => {
          const v = this.cowById(id);
          if (!v) return;
          boxes.push(`<li class="mofu-line">
            <img src="${esc(v.thumb)}" alt="" width="56" height="56" loading="lazy">
            <span class="mofu-line__text"><b>${esc(d.lineTitle)}, ${esc(v.name)}</b><small>${m(per)} ${esc(this.tr('each'))}${t.tierOn ? ` <s>${m(d.basePrice)}</s>` : ''}</small></span>
            <span class="mofu-line__qty">
              <button type="button" data-action="remove" data-id="${id}" aria-label="${esc(this.tr('remove_one', { name: v.name }))}">−</button>
              <span>${q}</span>
              <button type="button" data-action="add" data-id="${id}" aria-label="${esc(this.tr('add_one_more', { name: v.name }))}">+</button>
            </span></li>`);
        });
        const adding = [];
        this.extras.forEach((q, id) => {
          const hit = this.extraVariant(id);
          if (!hit) return;
          const name = hit.x.variants.length > 1 ? `${hit.x.title}, ${hit.v.title}` : hit.x.title;
          const full = !!hit.x.max && this.extraQty(hit.x) >= hit.x.max;
          adding.push(`<li class="mofu-line">
            <img src="${esc(hit.v.thumb || hit.x.image)}" alt="" width="56" height="56" loading="lazy">
            <span class="mofu-line__text"><b>${esc(name)}</b><small>${m(hit.v.price)}</small></span>
            <span class="mofu-line__qty">
              <button type="button" data-action="extra-remove" data-extra="${esc(hit.x.key)}" data-id="${id}" aria-label="${esc(this.tr('remove_one', { name }))}">−</button>
              <span>${q}</span>
              <button type="button" data-action="extra-add" data-extra="${esc(hit.x.key)}" data-id="${id}" aria-label="${esc(this.tr('add_one_more', { name }))}"${full ? ' disabled' : ''}>+</button>
            </span></li>`);
        });
        wrap.innerHTML = `
          ${boxes.length ? `<p class="mofu-line__label">${esc(this.tr('label_in_cart'))}</p><ul class="mofu-lines">${boxes.join('')}</ul>` : `<p class="mofu-small">${esc(this.tr('pick_first'))}</p>`}
          ${adding.length ? `<p class="mofu-line__label">${esc(this.tr('label_adding'))}</p><ul class="mofu-lines">${adding.join('')}</ul>` : ''}`;
      }

      // Add-ons the customer skipped, one tap away
      const list = this.querySelector('[data-addons]');
      if (list) {
        const offer = Array.isArray(d.reviewAddons) && d.reviewAddons.length ? d.reviewAddons : null;
        const rows = (d.extras || []).filter((x) => this.extraQty(x) === 0 && (!offer || offer.includes(x.key))).map((x) => {
          const vid = this.picked.get(x.key);
          const v = x.variants.find((xv) => xv.id === vid) || x.variants[0];
          const name = x.variants.length > 1 ? `${x.title}, ${v.title}` : x.title;
          return `<li class="mofu-line">
            <img src="${esc(v.thumb || x.image)}" alt="" width="56" height="56" loading="lazy">
            <span class="mofu-line__text"><b>${esc(name)}</b><small>${esc(x.note || '')}</small></span>
            <button type="button" class="mofu-line__add" data-action="extra-add" data-extra="${esc(x.key)}" data-id="${v.id}">+ ${m(v.price)}</button></li>`;
        });
        list.innerHTML = rows.join('');
        const box = this.querySelector('[data-addons-wrap]');
        if (box) box.hidden = rows.length === 0;
      }

      const sum = this.querySelector('[data-review-sum]');
      if (sum) {
        sum.innerHTML = `
          <div><span>${t.n} ${esc(this.unit(t.n))}</span><span>${m(t.boxes)}</span></div>
          ${t.herdSaving ? `<div class="is-saving"><span>${esc(this.tr('sum_herd_price'))}</span><span>−${m(t.herdSaving)}</span></div>` : ''}
          ${t.extras ? `<div><span>${esc(this.tr('sum_extras'))}</span><span>${m(t.extras)}</span></div>` : ''}
          <div><span>${esc(this.tr('sum_shipping'))}</span><span>${t.shipping ? m(t.shipping) : esc(this.tr('sum_free'))}</span></div>
          <div class="is-total"><span>${esc(this.tr('sum_total'))}</span><span>${m(t.total)}</span></div>
          ${t.toFree > 0 && t.items > 0 ? `<p class="mofu-small">${esc(this.tr('sum_add_amount', { amount: m(t.toFree) }))}</p>` : ''}
          ${t.toFreeQty > 0 && t.items > 0 ? `<p class="mofu-small">${esc(this.tr('sum_add_qty', { n: t.toFreeQty, unit: this.unit(t.toFreeQty) }))}</p>` : ''}`;
      }
    }

    renderDates() {
      const d = this.data;
      const now = new Date();
      const from = new Date(now.getTime() + d.deliveryMin * 864e5);
      const to = new Date(now.getTime() + d.deliveryMax * 864e5);
      const text = this.tr('arrives', { from: fmtDate(from, d.locale), to: fmtDate(to, d.locale) });
      document.querySelectorAll('[data-mofu-arrival]').forEach((el) => { el.textContent = text; });
      if (d.cutoff) {
        const cutoff = new Date(d.cutoff + 'T23:59:59');
        document.querySelectorAll('[data-mofu-cutoff]').forEach((el) => {
          el.hidden = now > cutoff;
        });
      }
    }

    /* ---------- cart ---------- */

    // Sets the cart to exactly what the builder shows, for our variants only. Other cart lines are left alone.
    async syncCart(withExtras, sections) {
      const updates = {};
      this.data.cows.forEach((c) => {
        const q = this.herd.get(c.id) || 0;
        if (q || this.inCart.get(c.id)) updates[c.id] = q;
      });
      if (withExtras) {
        (this.data.extras || []).forEach((x) => x.variants.forEach((v) => {
          const q = this.extras.get(v.id) || 0;
          if (q || this.inCart.get(v.id)) updates[v.id] = q;
        }));
      }
      if (!Object.keys(updates).length) return null;
      const body = { updates };
      if (sections) body.sections = sections;
      const res = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      let json;
      try {
        json = await res.json();
      } catch (e) {
        throw new Error(`the cart answered with HTTP ${res.status}`);
      }
      if (!res.ok) throw new Error(json.description || json.message || `HTTP ${res.status}`);
      this.inCart.clear();
      (json.items || []).forEach((item) => {
        if (this.cowById(item.variant_id) || this.extraVariant(item.variant_id)) {
          this.inCart.set(item.variant_id, (this.inCart.get(item.variant_id) || 0) + item.quantity);
        }
      });
      return json;
    }

    async submit(goCheckout) {
      if (!this.boxCount()) return;
      clearTimeout(this._syncTimer);
      const btns = this.querySelectorAll('[data-action="cart"],[data-action="next"]');
      btns.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
      this.say(this.tr(goCheckout ? 'to_checkout' : 'to_cart'));
      try {
        let json;
        try {
          json = await this.syncCart(true, goCheckout ? null : 'cart-drawer-section');
        } catch (err) {
          console.warn('mofu builder: cart update failed, using the checkout link instead', err);
          // Shopify's cart permalink builds the order on its own, so the customer still reaches checkout
          location.href = this.permalink();
          return;
        }
        if (goCheckout) {
          location.href = `${root()}checkout`;
          return;
        }
        this.say('');
        this.close();
        const opened = await this.openDrawer(json && json.sections && json.sections['cart-drawer-section']);
        if (!opened) location.href = `${root()}cart`;
      } catch (err) {
        this.say(this.tr('cart_error', { error: err.message }));
      } finally {
        btns.forEach((b) => { b.disabled = false; b.removeAttribute('aria-busy'); });
        this.render();
      }
    }

    // /cart/ID:QTY,ID:QTY goes straight to checkout with exactly these items
    permalink() {
      const parts = [];
      this.herd.forEach((q, id) => { if (q > 0) parts.push(`${id}:${q}`); });
      this.extras.forEach((q, id) => { if (q > 0) parts.push(`${id}:${q}`); });
      return `${root()}cart/${parts.join(',')}`;
    }

    async openDrawer(html) {
      const wrapper = document.getElementById('shopify-section-cart-drawer-section');
      if (!wrapper || !html) return false;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const fresh = tmp.querySelector('#shopify-section-cart-drawer-section') || tmp.firstElementChild;
      if (!fresh) return false;
      wrapper.replaceWith(fresh);
      try {
        const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
        document.querySelectorAll('.cart-bubble').forEach((b) => b.classList.toggle('visually-hidden', cart.item_count === 0));
        document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('hidden', cart.item_count === 0);
        });
      } catch (e) { /* the count catches up on the next page */ }
      await Promise.race([customElements.whenDefined('theme-drawer'), new Promise((r) => setTimeout(r, 1500))]);
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      return false;
    }
  }

  customElements.define('mofu-builder', MofuBuilder);
})();
